import {
  DoorCurrent,
  DoorState,
  FloorCurrent,
  isFloorCurrent,
} from "../interfaces/doors";

// VS133 periodic reports are expected at least every ten minutes. Keep the UI
// tolerance in one place so it can move with the device/reporting contract.
export const DOOR_READING_FRESHNESS_MS = 15 * 60 * 1000;
export const DOOR_FRESHNESS_TICK_MS = 60 * 1000;

export type DoorLiveStatus = "live" | "updating" | "delayed";

export type FloorAggregate = {
  current: FloorCurrent;
  source: "backend" | "fallback";
};

function timestampValue(value?: string): number | null {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function latestTimestamp(values: Array<string | undefined>): string {
  let latest = "";
  let latestValue = Number.NEGATIVE_INFINITY;

  values.forEach((value) => {
    const parsed = timestampValue(value);
    if (value && parsed !== null && parsed > latestValue) {
      latest = value;
      latestValue = parsed;
    }
  });

  return latest;
}

function earliestTimestamp(values: Array<string | undefined>): string {
  let earliest = "";
  let earliestValue = Number.POSITIVE_INFINITY;

  values.forEach((value) => {
    const parsed = timestampValue(value);
    if (value && parsed !== null && parsed < earliestValue) {
      earliest = value;
      earliestValue = parsed;
    }
  });

  return earliest;
}

function aggregateSnapshotTime(current: FloorCurrent): number {
  return (
    timestampValue(current.timeEnd) ??
    timestampValue(current.lastEventAt) ??
    timestampValue(current.baselineAt) ??
    Number.NEGATIVE_INFINITY
  );
}

function backendFloorCurrent(doors: DoorState[]): FloorCurrent | null {
  const candidates = doors
    .map((door) => door.floorCurrent)
    .filter(isFloorCurrent)
    .sort(
      (left, right) =>
        aggregateSnapshotTime(right) - aggregateSnapshotTime(left),
    );

  return candidates[0] || null;
}

function sameBaselineCurrents(doors: DoorState[]): DoorCurrent[] {
  const candidates = doors
    .map((door) => door.current)
    .filter((current): current is DoorCurrent => current !== null);
  const validBaselines = candidates
    .map((current) => timestampValue(current.timeStart))
    .filter((value): value is number => value !== null);

  if (validBaselines.length === 0) {
    return [];
  }

  // Do not mix yesterday's stale door with today's aggregate. The newest
  // baseline is authoritative and older-baseline sensors remain visible below.
  const newestBaseline = Math.max(...validBaselines);
  return candidates.filter(
    (current) => timestampValue(current.timeStart) === newestBaseline,
  );
}

function fallbackFloorCurrent(doors: DoorState[]): FloorCurrent {
  const currents = sameBaselineCurrents(doors);
  const totalIn = currents.reduce((total, current) => total + current.totalIn, 0);
  const totalOut = currents.reduce(
    (total, current) => total + current.totalOut,
    0,
  );
  const latestPeriodIn = currents.reduce(
    (total, current) => total + current.latestPeriodIn,
    0,
  );
  const latestPeriodOut = currents.reduce(
    (total, current) => total + current.latestPeriodOut,
    0,
  );
  const baselineAt = earliestTimestamp(
    currents.map((current) => current.timeStart),
  );
  const timeEnd = latestTimestamp(currents.map((current) => current.timeEnd));
  const latestPeriodAt = latestTimestamp(
    currents.map((current) => current.latestPeriodAt),
  );
  const net = totalIn - totalOut;

  return {
    scope: "floor",
    calculation: "daily_net",
    baseline: 0,
    baselineAt,
    sensorCount: currents.length,
    period: "day",
    timeStart: baselineAt,
    timeEnd,
    in: totalIn,
    out: totalOut,
    net,
    currentOccupancy: net,
    totalIn,
    totalOut,
    latestPeriodIn,
    latestPeriodOut,
    latestPeriodStart: earliestTimestamp(
      currents.map((current) => current.latestPeriodStart),
    ),
    latestPeriodEnd: latestTimestamp(
      currents.map((current) => current.latestPeriodEnd),
    ),
    latestPeriodAt,
    lastEventAt: latestTimestamp(
      currents.flatMap((current) => [
        current.lastEventAt,
        current.latestPeriodAt,
      ]),
    ),
  };
}

export function floorAggregate(doors: DoorState[]): FloorAggregate {
  const backend = backendFloorCurrent(doors);
  if (backend) {
    return { current: backend, source: "backend" };
  }

  return { current: fallbackFloorCurrent(doors), source: "fallback" };
}

function newestReading(values: Array<string | undefined>): number | null {
  const readings = values
    .map(timestampValue)
    .filter((value): value is number => value !== null);
  return readings.length > 0 ? Math.max(...readings) : null;
}

function supportedLiveEventTime(door: DoorState): string | undefined {
  return door.reportType === "line_crossing_trigger" ||
    door.reportType === "periodic"
    ? door.eventTime
    : undefined;
}

export function doorReadingTimes(
  doors: DoorState[],
  aggregate: FloorCurrent,
): Array<number | null> {
  const perDoorReadings = doors.map((door) =>
    newestReading([
      supportedLiveEventTime(door),
      door.current?.latestPeriodAt,
      door.current?.lastEventAt,
    ]),
  );

  if (perDoorReadings.some((reading) => reading !== null)) {
    // Once counter-specific freshness is available, retain missing values so a
    // healthy counter cannot conceal another counter that stopped reporting.
    return perDoorReadings;
  }

  // Compatibility fallback for an aggregate-only/legacy snapshot. Never mix
  // this floor-wide maximum into a partially populated per-door set.
  const aggregateReading = newestReading([
    aggregate.lastEventAt,
    aggregate.latestPeriodAt,
  ]);
  return doors.map(() => aggregateReading);
}

export function deriveDoorLiveStatus(
  isWebsocketOpen: boolean,
  readings: Array<number | null>,
  now: number,
): DoorLiveStatus {
  if (readings.length === 0) {
    return "updating";
  }

  const allReadingsAreFresh = readings.every((reading) => {
    if (reading === null) {
      return false;
    }
    const age = now - reading;
    return (
      age >= -DOOR_READING_FRESHNESS_MS &&
      age <= DOOR_READING_FRESHNESS_MS
    );
  });

  if (!allReadingsAreFresh) {
    return "delayed";
  }

  return isWebsocketOpen ? "live" : "updating";
}
